import { BellRing, MapPin, Repeat, CalendarCheck2, ListChecks, Users } from "lucide-react";
import { colourOf, fmtTime, weatherLook, REPEAT_LABELS } from "./palette";

const heading = (iso, todayISO) => {
  const when = new Date(`${iso}T12:00:00`);
  const nice = when.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });
  return iso === todayISO ? `Today · ${nice}` : nice;
};

export const AgendaList = ({ events, reminders, tasks, holidays, weather, accent, clock, todayISO, onPick }) => {
  const openTasks = (tasks || []).filter((t) => !t.done && t.due);
  const days = [...new Set([
    ...events.map((e) => e.day),
    ...reminders.map((r) => r.day),
    ...openTasks.map((t) => t.due),
    ...Object.keys(holidays || {}),
  ])]
    .filter((d) => d >= todayISO)
    .sort()
    .slice(0, 60);

  if (!days.length) {
    return (
      <div className="mt-6 rounded-3xl border border-white/10 bg-panel p-7 text-center" data-testid="agenda-list">
        <p className="text-sm text-ink-soft">Nothing coming up yet — add an event and it will show here.</p>
      </div>
    );
  }

  return (
    <div className="mt-6 space-y-3" data-testid="agenda-list">
      {days.map((day) => {
        const sky = weather[day];
        const Look = sky ? weatherLook(sky.code) : null;
        const feast = holidays?.[day];
        return (
          <div key={day} className="rounded-2xl border border-white/10 bg-panel p-4" data-testid={`agenda-day-${day}`}>
            <button
              onClick={() => onPick(day)}
              data-testid={`agenda-open-${day}`}
              className="flex w-full flex-wrap items-center gap-2 text-start"
            >
              <span className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: accent }}>
                {heading(day, todayISO)}
              </span>
              {feast && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-violet-300"
                      data-testid={`agenda-holiday-${day}`}>
                  <CalendarCheck2 className="h-3 w-3" /> {feast}
                </span>
              )}
              {Look && (
                <span className="ms-auto flex items-center gap-1 text-xs text-ink-soft" title={Look.blurb}>
                  <Look.Icon className="h-3.5 w-3.5" style={{ color: Look.tint }} /> {Look.label} · {sky.max}° / {sky.min}°
                </span>
              )}
            </button>
            <div className="mt-2 space-y-1.5">
              {events.filter((e) => e.day === day).map((ev) => (
                <div key={`${ev.event_id}-${ev.day}`} className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: colourOf(ev.colour) }} />
                  <span className="w-24 text-xs tabular-nums text-ink-soft">
                    {ev.all_day ? "All day" : fmtTime(ev.start, clock)}
                  </span>
                  <span dir="auto" className="font-semibold">{ev.title}</span>
                  {ev.location && (
                    <span className="inline-flex items-center gap-1 text-xs text-ink-soft">
                      <MapPin className="h-3 w-3" /> {ev.location}
                    </span>
                  )}
                  {ev.repeat !== "none" && (
                    <span className="inline-flex items-center gap-1 text-xs text-ink-soft">
                      <Repeat className="h-3 w-3" /> {REPEAT_LABELS[ev.repeat]}
                    </span>
                  )}
                  {ev.shared_from && (
                    <span className="inline-flex items-center gap-1 text-xs text-ink-soft">
                      <Users className="h-3 w-3" /> {ev.shared_from}
                    </span>
                  )}
                </div>
              ))}
              {reminders.filter((r) => r.day === day).map((r) => (
                <div key={r.reminder_id} className="flex flex-wrap items-center gap-2 text-sm">
                  <BellRing className="h-3.5 w-3.5" style={{ color: accent }} />
                  <span dir="auto" className={r.done ? "text-ink-soft line-through" : ""}>{r.text}</span>
                </div>
              ))}
              {openTasks.filter((t) => t.due === day).map((t) => (
                <div key={t.task_id} className="flex flex-wrap items-center gap-2 text-sm"
                     data-testid={`agenda-task-${t.task_id}`}>
                  <ListChecks className="h-3.5 w-3.5 text-ink-soft" />
                  <span dir="auto">{t.text}</span>
                  <span className="text-xs text-ink-soft">on my list</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};
